import { DragState, Layout, Piece } from './types';
import { traySlotH } from './renderer';

function canvasCoords(
  canvas: HTMLCanvasElement,
  clientX: number, clientY: number,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return { x: clientX - rect.left, y: clientY - rect.top };
}

function hitTraySlot(
  x: number, y: number,
  layout: Layout,
  tray: (Piece | null)[],
): number | null {
  const { trayY, trayCell, traySlotX, W } = layout;
  const slotW = W / 3;
  const slotH = traySlotH(trayCell);

  for (let i = 0; i < 3; i++) {
    if (!tray[i]) continue;
    const slotX = traySlotX[i] - slotW * 0.42;
    if (x >= slotX && x <= slotX + slotW * 0.84 &&
        y >= trayY  && y <= trayY + slotH) {
      return i;
    }
  }
  return null;
}

export interface DragHandle {
  reset: () => void;
}

export function attachDragListeners(
  canvas: HTMLCanvasElement,
  getLayout: () => Layout,
  getTray: () => (Piece | null)[],
  canDrag: () => boolean,
  onDragStart: (drag: DragState) => void,
  onDragMove:  (drag: DragState) => void,
  onDrop:      (drag: DragState) => void,
  onCancel:    () => void,
): DragHandle {
  let activeDrag: DragState | null = null;
  let activePointerId: number | null = null;

  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (activeDrag) return; // a pointer is already dragging, ignore others
    if (!canDrag()) return;
    const { x, y } = canvasCoords(canvas, e.clientX, e.clientY);
    const layout    = getLayout();
    const tray      = getTray();
    const idx       = hitTraySlot(x, y, layout, tray);
    if (idx === null) return;

    activeDrag = { trayIdx: idx, x, y };
    activePointerId = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    onDragStart(activeDrag);
  }, { passive: false });

  canvas.addEventListener('pointermove', (e) => {
    if (!activeDrag || e.pointerId !== activePointerId) return;
    e.preventDefault();
    const { x, y } = canvasCoords(canvas, e.clientX, e.clientY);
    activeDrag = { ...activeDrag, x, y };
    onDragMove(activeDrag);
  }, { passive: false });

  canvas.addEventListener('pointerup', (e) => {
    if (!activeDrag || e.pointerId !== activePointerId) return;
    e.preventDefault();
    const { x, y } = canvasCoords(canvas, e.clientX, e.clientY);
    const drag = { ...activeDrag, x, y };
    activeDrag = null;
    activePointerId = null;
    onDrop(drag);
  }, { passive: false });

  canvas.addEventListener('pointercancel', (e) => {
    if (e.pointerId !== activePointerId) return;
    activeDrag = null;
    activePointerId = null;
    onCancel();
  });

  return {
    reset: () => {
      activeDrag = null;
      activePointerId = null;
    },
  };
}
